(function () {
  "use strict";
  const configNode = document.getElementById("solarii-upload-config");
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  let base;
  try {
    base = new URL(config.endpoint, location.origin);
    if (base.protocol !== "https:" || base.origin !== location.origin || base.pathname !== "/apps/solarii-upload") return;
  } catch { return; }
  base = base.origin + base.pathname;
  let sessionPromise;
  const savedSessionKey = "solarii-upload-return-session-v1";
  try {
    const saved = JSON.parse(sessionStorage.getItem(savedSessionKey));
    if (saved && /^[a-f0-9-]{36}$/.test(saved.id) && /^[a-f0-9]{64}$/.test(saved.token) && saved.expiresAt * 1000 > Date.now() + 60000) {
      sessionPromise = Promise.resolve(saved);
    } else sessionStorage.removeItem(savedSessionKey);
  } catch {}
  const completed = new Map();
  const pending = new Map();

  function errorMessage(body, fallback) {
    return body && body.error && typeof body.error.message === "string" ? body.error.message : fallback;
  }

  async function getSession() {
    if (!sessionPromise) {
      sessionPromise = fetch(base + "/api/sessions", {
        method: "POST", credentials: "omit", redirect: "error",
        signal: AbortSignal.timeout(20000),
      }).then(async response => {
        const result = await response.json();
        if (!response.ok) throw new Error(errorMessage(result, "Could not start image upload. Please retry."));
        return result;
      }).catch(error => { sessionPromise = null; throw error; });
    }
    const session = await sessionPromise;
    if (session.expiresAt * 1000 <= Date.now() + 60000) {
      throw new Error("Your upload session has expired. Please reload this page and select your images again.");
    }
    return session;
  }

  function upload(file, id, onProgress) {
    if (completed.has(id)) return Promise.resolve(completed.get(id));
    if (pending.has(id)) return pending.get(id);
    const promise = (async () => {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || !file.size || file.size > 10 * 1024 * 1024) {
        throw new Error("Choose a JPG, PNG or WebP image, 10 MB or smaller.");
      }
      const session = await getSession();
      let result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", base + "/api/uploads");
        xhr.timeout = 120000;
        xhr.upload.onprogress = event => {
          if (onProgress && event.lengthComputable) onProgress(Math.min(99, Math.round(event.loaded / event.total * 100)));
        };
        xhr.onerror = () => reject(new Error("The upload connection failed. Check your connection and retry."));
        xhr.ontimeout = () => reject(new Error("The upload timed out. Please retry."));
        xhr.onabort = () => reject(new Error("The upload was cancelled."));
        xhr.onload = () => {
          let body;
          try { body = JSON.parse(xhr.responseText); } catch {
            reject(new Error("The upload service is unavailable. Please retry.")); return;
          }
          if (xhr.status < 200 || xhr.status >= 300) {
            reject(new Error(errorMessage(body, "The image could not be uploaded. Please retry."))); return;
          }
          if (body.id !== id || (xhr.status === 202 && body.status !== "processing")) {
            reject(new Error("The upload returned an invalid image link.")); return;
          }
          resolve(body);
        };
        const data = new FormData();
        data.append("session", session.id); data.append("token", session.token); data.append("id", id); data.append("file", file);
        xhr.send(data);
      });
      const deadline = Date.now() + 120000;
      while (result.status === "processing" && Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const response = await fetch(base + "/api/uploads/" + encodeURIComponent(id) + "/status", {
          method: "POST", credentials: "omit", redirect: "error",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session: session.id, token: session.token }), signal: AbortSignal.timeout(20000),
        });
        result = await response.json();
        if (!response.ok) throw new Error(errorMessage(result, "The image is still processing. Please retry."));
      }
      if (result.status !== "ready") throw new Error("Your image is still processing. Please retry in a moment.");
      let remote;
      try { remote = new URL(result.url); } catch { throw new Error("The upload returned an invalid image link."); }
      if (result.id !== id || remote.origin !== "https://cdn.shopify.com" || remote.username || remote.password ||
          !new RegExp("^/s/files/1/0642/6941/8596/files/solarii-inquiry-" + id + "\\.(png|jpg|webp)$").test(remote.pathname)) {
        throw new Error("The upload returned an invalid image link.");
      }
      completed.set(id, result);
      if (onProgress) onProgress(100);
      return result;
    })().finally(() => pending.delete(id));
    pending.set(id, promise);
    return promise;
  }

  async function remove(id) {
    if (pending.has(id)) await pending.get(id).catch(() => {});
    const session = await getSession();
    const response = await fetch(base + "/api/uploads/" + encodeURIComponent(id) + "/remove", {
      method: "POST", credentials: "omit", redirect: "error",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session: session.id, token: session.token }),
      signal: AbortSignal.timeout(20000),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(errorMessage(body, "Could not remove this image. Please retry."));
    }
    completed.delete(id);
  }

  async function saveSessionForReturn() {
    if (!sessionPromise) return;
    const session = await sessionPromise;
    try { sessionStorage.setItem(savedSessionKey, JSON.stringify(session)); } catch {}
  }
  function clearSavedSession() {
    try { sessionStorage.removeItem(savedSessionKey); } catch {}
  }
  window.SolariiUploads = Object.freeze({ upload, remove, saveSessionForReturn, clearSavedSession });
})();
